import { useSyncExternalStore } from 'react'
import { MediaPermissionDevice, MediaPermissionState } from '../../../../pure-js/types/AppAssistant'

interface MediaPermissions {
  camera: MediaPermissionState
  microphone: MediaPermissionState
  isLoading: boolean
}

type PermissionQueryResult = { state: MediaPermissionState; status?: PermissionStatus }

const INITIAL_PERMISSIONS: MediaPermissions = {
  camera: MediaPermissionState.CHECKING,
  microphone: MediaPermissionState.CHECKING,
  isLoading: true
}

let snapshot = INITIAL_PERMISSIONS
let refreshInFlight: Promise<void> | null = null
let observerCleanup: (() => void) | null = null
let permissionStatusCleanup: (() => void) | null = null
const subscribers = new Set<() => void>()

const publish = (next: MediaPermissions): void => {
  const isPermissionStateUnchanged =
    snapshot.camera === next.camera && snapshot.microphone === next.microphone && snapshot.isLoading === next.isLoading
  if (isPermissionStateUnchanged) return
  snapshot = next
  subscribers.forEach((notify) => notify())
}

const resolveUnsupportedPermission = (
  permission: PermissionQueryResult,
  hasNamedDevice: boolean
): PermissionQueryResult => {
  if (permission.status) return permission
  return { state: hasNamedDevice ? MediaPermissionState.GRANTED : MediaPermissionState.PROMPT }
}

const queryPermission = async (name: MediaPermissionDevice): Promise<PermissionQueryResult> => {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) {
    return { state: MediaPermissionState.PROMPT }
  }
  try {
    const status = await navigator.permissions.query({ name: name as PermissionName })
    return { state: status.state as MediaPermissionState, status }
  } catch {
    return { state: MediaPermissionState.PROMPT }
  }
}

const inferUnsupportedPermissions = async (
  camera: PermissionQueryResult,
  microphone: PermissionQueryResult
): Promise<[PermissionQueryResult, PermissionQueryResult]> => {
  if (camera.status && microphone.status) return [camera, microphone]
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) return [camera, microphone]

  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    const hasNamedCamera = devices.some((device) => device.kind === 'videoinput' && Boolean(device.label))
    const hasNamedMicrophone = devices.some((device) => device.kind === 'audioinput' && Boolean(device.label))
    return [
      resolveUnsupportedPermission(camera, hasNamedCamera),
      resolveUnsupportedPermission(microphone, hasNamedMicrophone)
    ]
  } catch {
    return [camera, microphone]
  }
}

const observePermissionStatuses = (statuses: PermissionStatus[]): void => {
  permissionStatusCleanup?.()
  const onChange = () => void refreshMediaPermissions()
  statuses.forEach((status) => status.addEventListener?.('change', onChange))
  permissionStatusCleanup = () => {
    statuses.forEach((status) => status.removeEventListener?.('change', onChange))
  }
}

const refreshMediaPermissions = (): Promise<void> => {
  if (refreshInFlight) return refreshInFlight

  const refresh = async () => {
    const queried = await Promise.all([
      queryPermission(MediaPermissionDevice.CAMERA),
      queryPermission(MediaPermissionDevice.MICROPHONE)
    ])
    const [camera, microphone] = await inferUnsupportedPermissions(queried[0], queried[1])
    observePermissionStatuses([camera.status, microphone.status].filter(Boolean) as PermissionStatus[])
    publish({ camera: camera.state, microphone: microphone.state, isLoading: false })
  }

  refreshInFlight = refresh().finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

const startObserving = (): (() => void) => {
  const mediaDevices = typeof navigator === 'undefined' ? undefined : navigator.mediaDevices
  const documentTarget = typeof document === 'undefined' ? undefined : document
  const onDeviceChange = () => void refreshMediaPermissions()
  const onVisibilityChange = () => {
    if (documentTarget?.visibilityState === 'visible') void refreshMediaPermissions()
  }

  mediaDevices?.addEventListener?.('devicechange', onDeviceChange)
  documentTarget?.addEventListener('visibilitychange', onVisibilityChange)
  void refreshMediaPermissions()

  return () => {
    mediaDevices?.removeEventListener?.('devicechange', onDeviceChange)
    documentTarget?.removeEventListener('visibilitychange', onVisibilityChange)
    permissionStatusCleanup?.()
    permissionStatusCleanup = null
  }
}

const subscribe = (notify: () => void): (() => void) => {
  subscribers.add(notify)
  if (subscribers.size === 1) observerCleanup = startObserving()

  return () => {
    subscribers.delete(notify)
    if (subscribers.size > 0) return
    observerCleanup?.()
    observerCleanup = null
  }
}

const getSnapshot = (): MediaPermissions => snapshot

export const useMediaPermissions = (): MediaPermissions =>
  useSyncExternalStore(subscribe, getSnapshot, () => INITIAL_PERMISSIONS)
